import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import AdminNavBar from "../components/AdminNavBar";
import Toast from "../components/Toast";
import { useConfirmDialog } from "../components/ConfirmDialogProvider";
import {
  AdminEmptyState,
  AdminLoadingState,
  AdminPageBody,
  AdminSectionHeader,
  AdminTablePanel,
  adminButton,
  adminPrimaryButton,
} from "../components/AdminPageShell";
import StaffPagination from "./components/staff/StaffPagination";
import { useAdminMarketplace } from "../store/admin";
import { htmlToText } from "../utils/toDisplayString";
import {
  MARKETPLACE_TYPES,
  RESOURCE_STATUS_OPTIONS,
  formatCurrency,
  formatDate,
  purchaseItemsLabel,
  statusBadgeClass,
} from "./components/marketplace/marketplaceUtils";

const ORDERS_LIMIT = 8;

export default function MarketplaceManagement() {
  const navigate = useNavigate();
  const confirm = useConfirmDialog();

  const [activeType, setActiveType] = useState(MARKETPLACE_TYPES[0].key);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState(null);
  const [isSuccess, setIsSuccess] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [resourceForm, setResourceForm] = useState({
    title: "",
    summary: "",
    description: "",
    price: "",
    coverImage: "",
    academicLevel: "",
    fieldOfStudy: "",
    fileFormat: "",
  });

  const {
    marketplacePurchases,
    marketplacePurchasesLoading,
    marketplacePurchasesError,
    marketplacePurchasesTotal,
    fetchMarketplacePurchases,
    marketplaceModerationCounts,
    fetchMarketplaceModerationCounts,
    marketplaceResources,
    marketplaceResourcesLoading,
    marketplaceResourcesError,
    marketplaceResourcesTotal,
    marketplaceResourcesPages,
    fetchMarketplaceResources,
    marketplaceResourceDetail,
    marketplaceResourceDetailLoading,
    marketplaceResourceDetailError,
    fetchMarketplaceResource,
    updateMarketplaceResource,
    approveMarketplaceResource,
    rejectMarketplaceResource,
    restoreMarketplaceResource,
    withdrawMarketplaceResource,
    marketplaceMutationLoading,
  } = useAdminMarketplace();

  const showToast = useCallback((message, success = true) => {
    setToast(message);
    setIsSuccess(success);
    setTimeout(() => setToast(null), 5000);
  }, []);

  const loadResources = useCallback(async () => {
    const result = await fetchMarketplaceResources(activeType, {
      page,
      limit: 10,
      search: search || undefined,
      status: statusFilter || undefined,
      sort,
    });
    if (!result.success) {
      showToast(result.error || "Failed to load marketplace resources", false);
    }
  }, [activeType, fetchMarketplaceResources, page, search, showToast, sort, statusFilter]);

  useEffect(() => {
    loadResources();
  }, [loadResources]);

  useEffect(() => {
    fetchMarketplacePurchases({ page: 1, limit: ORDERS_LIMIT });
    fetchMarketplaceModerationCounts();
  }, [fetchMarketplacePurchases, fetchMarketplaceModerationCounts]);

  const revenue = useMemo(() => {
    return marketplacePurchases
      .filter((purchase) => purchase.status === "COMPLETED")
      .reduce((total, purchase) => total + (Number(purchase.amount) || 0), 0);
  }, [marketplacePurchases]);

  const activeTypeMeta = MARKETPLACE_TYPES.find((type) => type.key === activeType);

  const runMutation = async (mutation, successMessage) => {
    const result = await mutation();
    if (!result.success) {
      showToast(result.error || "Action failed", false);
      return false;
    }
    showToast(successMessage, true);
    await Promise.all([loadResources(), fetchMarketplaceModerationCounts()]);
    return true;
  };

  const handleView = async (resource) => {
    setDetailOpen(true);
    await fetchMarketplaceResource(activeType, resource.id);
  };

  const handleEditOpen = (resource) => {
    setEditingResource(resource);
    setResourceForm({
      title: resource.title || "",
      summary: resource.summary || "",
      description: resource.description || "",
      price: resource.price ?? "",
      coverImage: resource.coverImage || "",
      academicLevel: resource.academicLevel || "",
      fieldOfStudy: resource.fieldOfStudy || "",
      fileFormat: resource.fileFormat || "",
    });
    setEditOpen(true);
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    if (!editingResource) return;
    const payload = {
      title: resourceForm.title.trim(),
      summary: resourceForm.summary.trim() || null,
      description: resourceForm.description.trim() || null,
      coverImage: resourceForm.coverImage.trim() || null,
      academicLevel: resourceForm.academicLevel || null,
      fieldOfStudy: resourceForm.fieldOfStudy.trim() || null,
      fileFormat: resourceForm.fileFormat || null,
    };
    if (resourceForm.price !== "") payload.price = Number(resourceForm.price);
    const ok = await runMutation(
      () => updateMarketplaceResource(activeType, editingResource.id, payload),
      "Resource updated"
    );
    if (ok) {
      setEditOpen(false);
      setEditingResource(null);
    }
  };

  const handleApprove = (resource) =>
    confirm({
      title: `Approve "${resource.title}"?`,
      description: "The resource will be published to the marketplace.",
      confirmLabel: "Approve",
      onConfirm: async () => {
        const ok = await runMutation(
          () => approveMarketplaceResource(activeType, resource.id),
          "Resource approved"
        );
        if (!ok) throw new Error("Failed to approve resource.");
      },
    });

  const handleReject = (resource) => {
    const reason = window.prompt(`Reason for rejecting "${resource.title}"?`, "");
    if (reason === null) return;
    return runMutation(
      () => rejectMarketplaceResource(activeType, resource.id, reason.trim()),
      "Resource rejected"
    );
  };

  const handleWithdraw = (resource) =>
    confirm({
      title: `Withdraw "${resource.title}"?`,
      description: "The resource will be removed from the marketplace.",
      confirmLabel: "Withdraw",
      onConfirm: async () => {
        const ok = await runMutation(
          () => withdrawMarketplaceResource(activeType, resource.id),
          "Resource withdrawn"
        );
        if (!ok) throw new Error("Failed to withdraw resource.");
      },
    });

  const handleRestore = (resource) =>
    confirm({
      title: `Restore "${resource.title}"?`,
      description: "The resource will be published again.",
      confirmLabel: "Restore",
      onConfirm: async () => {
        const ok = await runMutation(
          () => restoreMarketplaceResource(activeType, resource.id),
          "Resource restored"
        );
        if (!ok) throw new Error("Failed to restore resource.");
      },
    });

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
  };

  const summaryCards = [
    {
      label: "Revenue (loaded orders)",
      value: formatCurrency(revenue),
    },
    {
      label: "Total Purchases",
      value: marketplacePurchasesTotal ?? 0,
    },
    {
      label: "Pending Moderation",
      value: marketplaceModerationCounts?.total ?? "—",
    },
    {
      label: `${activeTypeMeta?.label || "Resources"}`,
      value: marketplaceResourcesTotal ?? 0,
    },
  ];

  return (
    <div>
      <AdminNavBar title="Marketplace Management" />
      <AdminPageBody>
        {toast && <Toast message={toast} onClose={() => setToast(null)} isSuccess={isSuccess} />}

        <AdminSectionHeader
          title="Marketplace Management"
          description="Moderate marketplace resources and review recent purchases."
          count={`${marketplaceResourcesTotal} ${activeTypeMeta?.label?.toLowerCase() || "resources"}`}
          actions={
            <button className={adminButton} onClick={() => navigate("/admin/invoice-management")}>
              View Revenue
            </button>
          }
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map((card) => (
            <div
              key={card.label}
              className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {card.label}
              </p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{card.value}</p>
            </div>
          ))}
        </div>

        <AdminSectionHeader
          title="Recent Purchases"
          description="The latest marketplace purchases across all resource types."
          count={`${marketplacePurchasesTotal ?? 0} total`}
        />

        {marketplacePurchasesError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
            {marketplacePurchasesError}
          </div>
        )}

        {marketplacePurchasesLoading ? (
          <AdminLoadingState />
        ) : marketplacePurchases.length === 0 ? (
          <AdminEmptyState title="No purchases yet" />
        ) : (
          <AdminTablePanel>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Reference
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Learner
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Item
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Amount
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Status
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {marketplacePurchases.map((purchase) => (
                    <tr key={purchase.id} className="border-b border-slate-100">
                      <td className="px-4 py-2 font-mono text-xs">{purchase.reference}</td>
                      <td className="px-4 py-2">
                        <div className="text-sm text-slate-800">{purchase.learner?.name || "—"}</div>
                        <div className="text-xs text-slate-500">{purchase.learner?.email || ""}</div>
                      </td>
                      <td className="px-4 py-2 text-sm">{purchaseItemsLabel(purchase)}</td>
                      <td className="px-4 py-2 text-sm font-semibold">
                        {formatCurrency(purchase.amount, purchase.currency || "NGN")}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`badge ${statusBadgeClass(purchase.status)}`}>
                          {purchase.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500">
                        {formatDate(purchase.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AdminTablePanel>
        )}

        <AdminSectionHeader
          title="Moderate Resources"
          description="Review submitted resources and publish, reject or withdraw them."
          count={`${marketplaceResourcesTotal} result${marketplaceResourcesTotal === 1 ? "" : "s"}`}
        />

        <div className="flex flex-wrap gap-2">
          {MARKETPLACE_TYPES.map((type) => (
            <button
              key={type.key}
              onClick={() => {
                setActiveType(type.key);
                setPage(1);
              }}
              className={
                activeType === type.key
                  ? "rounded-lg bg-[#16213A] px-4 py-2 text-sm font-semibold text-white"
                  : adminButton
              }
            >
              {type.label}
              {marketplaceModerationCounts?.[type.key] ? (
                <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-700">
                  {marketplaceModerationCounts[type.key]}
                </span>
              ) : null}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <form onSubmit={handleSearchSubmit} className="flex gap-2">
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by title..."
              className="h-10 w-64 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#16213A]"
            />
            <button type="submit" className={adminButton}>
              Search
            </button>
            {search && (
              <button
                type="button"
                className={adminButton}
                onClick={() => {
                  setSearch("");
                  setPage(1);
                }}
              >
                Clear
              </button>
            )}
          </form>

          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(1);
            }}
            className="h-10 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#16213A]"
          >
            {RESOURCE_STATUS_OPTIONS.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={sort}
            onChange={(event) => {
              setSort(event.target.value);
              setPage(1);
            }}
            className="h-10 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#16213A]"
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="price_asc">Price (low to high)</option>
            <option value="price_desc">Price (high to low)</option>
          </select>
        </div>

        {marketplaceResourcesError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
            {marketplaceResourcesError}
          </div>
        )}

        {marketplaceResourcesLoading ? (
          <AdminLoadingState />
        ) : marketplaceResources.length === 0 ? (
          <AdminEmptyState
            title="No resources found"
            description="Try a different status or search term."
          />
        ) : (
          <AdminTablePanel>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Title
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Publisher
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Price
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Status
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Submitted
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold uppercase text-slate-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {marketplaceResources.map((resource) => {
                    const status = String(resource.status || "").toUpperCase();
                    const canApprove = status !== "APPROVED" && status !== "WITHDRAWN";
                    const canWithdraw = status === "APPROVED" || status === "PENDING";
                    const canRestore = status === "WITHDRAWN" || status === "REJECTED";
                    return (
                      <tr key={resource.id} className="border-b border-slate-100">
                        <td className="px-4 py-2">
                          <div className="text-sm font-medium text-slate-800">{resource.title}</div>
                          <div className="text-xs text-slate-500">{resource.slug}</div>
                        </td>
                        <td className="px-4 py-2 text-sm">{resource.publisher?.name || "—"}</td>
                        <td className="px-4 py-2 text-sm">
                          {formatCurrency(resource.price)}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`badge ${statusBadgeClass(status)}`}>{status}</span>
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-500">
                          {formatDate(resource.submittedAt || resource.createdAt)}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap justify-end gap-2">
                            <button
                              className="text-xs font-semibold text-[#16213A] hover:text-[#203052]"
                              onClick={() => handleView(resource)}
                            >
                              View
                            </button>
                            <button
                              className="text-xs font-semibold text-slate-700 hover:text-slate-900"
                              onClick={() => handleEditOpen(resource)}
                            >
                              Edit
                            </button>
                            {canApprove && (
                              <button
                                className="text-xs font-semibold text-emerald-700 hover:text-emerald-900"
                                disabled={marketplaceMutationLoading}
                                onClick={() => handleApprove(resource)}
                              >
                                Approve
                              </button>
                            )}
                            {status !== "REJECTED" && (
                              <button
                                className="text-xs font-semibold text-amber-700 hover:text-amber-900"
                                disabled={marketplaceMutationLoading}
                                onClick={() => handleReject(resource)}
                              >
                                Reject
                              </button>
                            )}
                            {canWithdraw && (
                              <button
                                className="text-xs font-semibold text-slate-700 hover:text-slate-900"
                                disabled={marketplaceMutationLoading}
                                onClick={() => handleWithdraw(resource)}
                              >
                                Withdraw
                              </button>
                            )}
                            {canRestore && (
                              <button
                                className="text-xs font-semibold text-blue-700 hover:text-blue-900"
                                disabled={marketplaceMutationLoading}
                                onClick={() => handleRestore(resource)}
                              >
                                Restore
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </AdminTablePanel>
        )}

        {marketplaceResourcesPages > 1 && (
          <StaffPagination
            page={page}
            lastPage={marketplaceResourcesPages}
            onPageChange={setPage}
          />
        )}

        {detailOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-bold">Resource Details</h3>
                <button
                  type="button"
                  onClick={() => setDetailOpen(false)}
                  className="text-slate-400 hover:text-slate-700"
                >
                  ✕
                </button>
              </div>
              {marketplaceResourceDetailLoading ? (
                <AdminLoadingState />
              ) : marketplaceResourceDetailError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                  {marketplaceResourceDetailError}
                </div>
              ) : marketplaceResourceDetail ? (
                <div className="space-y-3 text-sm">
                  <h4 className="text-base font-semibold">
                    {htmlToText(marketplaceResourceDetail.title)}
                  </h4>
                  <p className="text-slate-600">
                    {htmlToText(marketplaceResourceDetail.summary) || "No summary."}
                  </p>
                  <p className="whitespace-pre-line text-slate-600">
                    {htmlToText(marketplaceResourceDetail.description) || "No description."}
                  </p>
                  <dl className="grid grid-cols-2 gap-3">
                    <div>
                      <dt className="text-xs font-semibold uppercase text-slate-500">Status</dt>
                      <dd>{marketplaceResourceDetail.status}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase text-slate-500">Price</dt>
                      <dd>{formatCurrency(marketplaceResourceDetail.price)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase text-slate-500">Publisher</dt>
                      <dd>{marketplaceResourceDetail.publisher?.name || "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase text-slate-500">Downloads</dt>
                      <dd>{marketplaceResourceDetail.downloads ?? 0}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase text-slate-500">Academic Level</dt>
                      <dd>{marketplaceResourceDetail.academicLevel || "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase text-slate-500">Field of Study</dt>
                      <dd>{marketplaceResourceDetail.fieldOfStudy || "—"}</dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="text-xs font-semibold uppercase text-slate-500">
                        Departments
                      </dt>
                      <dd>
                        {(marketplaceResourceDetail.departments || [])
                          .map((department) => department.name)
                          .join(", ") || "—"}
                      </dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="text-xs font-semibold uppercase text-slate-500">
                        Rejection Reason
                      </dt>
                      <dd>{marketplaceResourceDetail.rejectionReason || "—"}</dd>
                    </div>
                  </dl>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {editOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-bold">Edit Resource</h3>
                <button
                  type="button"
                  onClick={() => {
                    setEditOpen(false);
                    setEditingResource(null);
                  }}
                  className="text-slate-400 hover:text-slate-700"
                >
                  ✕
                </button>
              </div>
              <form onSubmit={handleEditSubmit} className="grid gap-4 sm:grid-cols-2">
                <label className="flex flex-col gap-1 text-xs font-semibold uppercase text-slate-500 sm:col-span-2">
                  Title
                  <input
                    value={resourceForm.title}
                    onChange={(event) =>
                      setResourceForm({ ...resourceForm, title: event.target.value })
                    }
                    required
                    className="h-10 rounded-lg border border-slate-300 px-3 text-sm font-normal normal-case"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs font-semibold uppercase text-slate-500">
                  Price
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={resourceForm.price}
                    onChange={(event) =>
                      setResourceForm({ ...resourceForm, price: event.target.value })
                    }
                    className="h-10 rounded-lg border border-slate-300 px-3 text-sm font-normal normal-case"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs font-semibold uppercase text-slate-500">
                  Cover image URL
                  <input
                    value={resourceForm.coverImage}
                    onChange={(event) =>
                      setResourceForm({ ...resourceForm, coverImage: event.target.value })
                    }
                    className="h-10 rounded-lg border border-slate-300 px-3 text-sm font-normal normal-case"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs font-semibold uppercase text-slate-500">
                  Academic level
                  <select
                    value={resourceForm.academicLevel}
                    onChange={(event) =>
                      setResourceForm({ ...resourceForm, academicLevel: event.target.value })
                    }
                    className="h-10 rounded-lg border border-slate-300 px-3 text-sm font-normal normal-case"
                  >
                    <option value="">Not set</option>
                    {["ND", "HND", "NCE", "BSC", "MSC", "PGD", "PHD", "OTHER"].map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-xs font-semibold uppercase text-slate-500">
                  File format
                  <select
                    value={resourceForm.fileFormat}
                    onChange={(event) =>
                      setResourceForm({ ...resourceForm, fileFormat: event.target.value })
                    }
                    className="h-10 rounded-lg border border-slate-300 px-3 text-sm font-normal normal-case"
                  >
                    <option value="">Not set</option>
                    {["PDF", "DOCX", "OTHER"].map((format) => (
                      <option key={format} value={format}>
                        {format}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-xs font-semibold uppercase text-slate-500 sm:col-span-2">
                  Field of study
                  <input
                    value={resourceForm.fieldOfStudy}
                    onChange={(event) =>
                      setResourceForm({ ...resourceForm, fieldOfStudy: event.target.value })
                    }
                    className="h-10 rounded-lg border border-slate-300 px-3 text-sm font-normal normal-case"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs font-semibold uppercase text-slate-500 sm:col-span-2">
                  Summary
                  <textarea
                    rows={2}
                    value={resourceForm.summary}
                    onChange={(event) =>
                      setResourceForm({ ...resourceForm, summary: event.target.value })
                    }
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal normal-case"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs font-semibold uppercase text-slate-500 sm:col-span-2">
                  Description
                  <textarea
                    rows={5}
                    value={resourceForm.description}
                    onChange={(event) =>
                      setResourceForm({ ...resourceForm, description: event.target.value })
                    }
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal normal-case"
                  />
                </label>
                <div className="flex gap-2 sm:col-span-2">
                  <button
                    type="submit"
                    className={adminPrimaryButton}
                    disabled={marketplaceMutationLoading}
                  >
                    {marketplaceMutationLoading ? "Saving…" : "Save Changes"}
                  </button>
                  <button
                    type="button"
                    className={adminButton}
                    onClick={() => {
                      setEditOpen(false);
                      setEditingResource(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </AdminPageBody>
    </div>
  );
}
